import { useCallback, useEffect, useState } from 'react';
import { readPreference, writePreference } from '../utils/storage';

export type ThemePreference = 'light' | 'dark' | 'system';

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

export function applyTheme(preference: ThemePreference): void {
  const dark = preference === 'dark' || (preference === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

function initialPreference(): ThemePreference {
  const stored = readPreference('theme');
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

/** Theme preference persisted per browser, following the OS setting by default. */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(initialPreference);

  useEffect(() => {
    applyTheme(preference);
    if (preference !== 'system' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const update = useCallback((next: ThemePreference) => {
    writePreference('theme', next);
    setPreference(next);
  }, []);

  return { preference, setPreference: update };
}
